import { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { deduplicateRequest, createRequestKey } from '../../utils/requestDeduplication';

const API_URL = '/api';

// Flag global para evitar múltiples cargas iniciales
let hasLoadedClients = false;

export const useClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', phone: '', type: 'cliente' });
  const [currentClientPage, setCurrentClientPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  
  const { csrfToken, refreshCsrfToken } = useAuth();

  const ensureFreshCsrfToken = async () => {
    try {
      const result = await refreshCsrfToken();
      if (!result.success) {
        console.error('Error al refrescar el token CSRF');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error al refrescar el token CSRF:', error);
      return false;
    }
  };

  const fetchClients = useCallback(async (page = 1, limit = itemsPerPage, search = clientSearchTerm) => {
    const requestKey = createRequestKey('fetchClients', { page, limit, search });

    return deduplicateRequest(requestKey, async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ page, limit });
        if (search) params.append('search', search);

        const response = await axios.get(`${API_URL}/clients?${params}`);

        // Marcar que ya se han cargado clientes
        hasLoadedClients = true;

        // Manejar respuesta paginada del servidor
        if (response.data.clients && response.data.pagination) {
          setClients(response.data.clients);
          setPaginationInfo(response.data.pagination);
        } else {
          // Fallback para compatibilidad
          setClients(response.data);
        }

        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al cargar los clientes. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    });
  }, [itemsPerPage, clientSearchTerm]); // Solo depende de itemsPerPage y clientSearchTerm

  // Función para cargar TODOS los clientes sin paginación
  const fetchAllClients = useCallback(async () => {
    const requestKey = createRequestKey('fetchAllClients', {});

    return deduplicateRequest(requestKey, async () => {
      try {
        setLoading(true);
        // Sin parámetros de paginación para obtener todos
        const response = await axios.get(`${API_URL}/clients?all=true`);

        // Marcar que ya se han cargado clientes
        hasLoadedClients = true;

        // Manejar respuesta completa
        if (response.data.clients) {
          setClients(response.data.clients);
        } else {
          setClients(response.data);
        }

        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al cargar todos los clientes. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    });
  }, []); // No tiene dependencias - siempre hace la misma petición

  const handleSaveClient = async () => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const clientData = {
        name: newClient.name,
        phone: newClient.phone,
        type: newClient.type || 'cliente'
      };

      if (newClient.id) {
        await axios.put(`${API_URL}/clients/${newClient.id}`, clientData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      } else {
        await axios.post(`${API_URL}/clients`, clientData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      }

      await fetchClients();
      setNewClient({ name: '', phone: '', type: 'cliente' });
      setShowClientForm(false);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al guardar el cliente. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este cliente?')) {
      try {
        setLoading(true);
        
        if (!await ensureFreshCsrfToken()) {
          setError('Error al obtener un token CSRF válido. Intente recargar la página.');
          setLoading(false);
          return;
        }
        
        await axios.delete(`${API_URL}/clients/${clientId}`, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });

        await fetchClients();
        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al eliminar el cliente. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditClient = (client) => {
    setNewClient({
      id: client.id,
      name: client.name,
      phone: client.phone,
      type: client.type || 'cliente'
    });
    setShowClientForm(true);
  };


  const handleSearch = (searchTerm) => {
    setClientSearchTerm(searchTerm);
    setCurrentClientPage(1);
    fetchClients(1, itemsPerPage, searchTerm);
  };

  const handleNextPage = () => {
    if (paginationInfo.hasNextPage) {
      const nextPage = currentClientPage + 1;
      setCurrentClientPage(nextPage);
      fetchClients(nextPage, itemsPerPage, clientSearchTerm);
    }
  };

  const handlePrevPage = () => {
    if (paginationInfo.hasPrevPage) {
      const prevPage = currentClientPage - 1;
      setCurrentClientPage(prevPage);
      fetchClients(prevPage, itemsPerPage, clientSearchTerm);
    }
  };

  const handlePageChange = (page) => {
    setCurrentClientPage(page);
    fetchClients(page, itemsPerPage, clientSearchTerm);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentClientPage(1);
    fetchClients(1, newItemsPerPage, clientSearchTerm);
  };

  return {
    clients,
    loading,
    error,
    setError,
    showClientForm,
    setShowClientForm,
    newClient,
    setNewClient,
    currentClientPage,
    itemsPerPage,
    clientSearchTerm,
    setClientSearchTerm,
    paginationInfo,
    fetchClients,
    fetchAllClients, // ✅ Nueva función para cargar todos los clientes
    handleSaveClient,
    handleDeleteClient,
    handleEditClient,
    handleSearch,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
    handleItemsPerPageChange
  };
};

